import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, Switch, Row, Col, message } from 'antd';
import { useSaveBiometricDeviceMutation } from '../../services/biometric.service';
import { useGetBranchesQuery } from '../../services/settings.service';

const { Option } = Select;

const BiometricDeviceModal = ({ visible, device, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [saveDevice, { isLoading }] = useSaveBiometricDeviceMutation();
  const { data: branchesData } = useGetBranchesQuery();

  const branches = branchesData?.data || [];

  useEffect(() => {
    if (visible) {
      if (device) {
        // Editing existing device - map snake_case to camelCase for form
        form.setFieldsValue({
          device_name: device.device_name,
          device_type: device.device_type,
          ip_address: device.ip_address,
          port: device.port,
          serial_number: device.serial_number,
          location: device.location,
          is_active: device.is_active,
          branch_id: device.branch_id,
          push_users_to_device: device.push_users_to_device,
        });
      } else {
        // Adding new device
        form.resetFields();
        form.setFieldsValue({
          port: 4370, // Default port for ZKTeco
          is_active: true,
          push_users_to_device: true,
        });
      }
    }
  }, [visible, device, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Build payload with snake_case field names (matching backend DTO)
      const payload = {
        device_id: values.device_id || `DEV-${Date.now()}`, // Generate ID if not provided
        device_name: values.device_name,
        device_type: values.device_type,
        ip_address: values.ip_address,
        port: Number(values.port),
        serial_number: values.serial_number,
        location: values.location,
        is_active: values.is_active,
        branch_id: values.branch_id,
        push_users_to_device: values.push_users_to_device,
      };

      if (device?.id) {
        payload.id = device.id;
      }

      await saveDevice(payload).unwrap();

      message.success(`Device ${device ? 'updated' : 'added'} successfully`);
      onSuccess();
    } catch (error) {
      if (error.errorFields) {
        // Validation error
        return;
      }
      message.error(error?.data?.message || `Failed to ${device ? 'update' : 'add'} device`);
    }
  };

  const handleDeviceTypeChange = (value) => {
    // Set default ports based on device type
    if (value === 'zkteco') {
      form.setFieldsValue({ port: 4370 });
    } else if (value === 'realand') {
      form.setFieldsValue({ port: 80 });
    }
  };

  return (
    <Modal
      title={device ? 'Edit Biometric Device' : 'Add Biometric Device'}
      open={visible}
      onOk={handleSubmit}
      onCancel={onClose}
      okText={device ? 'Update' : 'Add'}
      confirmLoading={isLoading}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="device_name"
              label="Device Name"
              rules={[
                { required: true, message: 'Please enter device name' },
                { min: 3, message: 'Name must be at least 3 characters' },
              ]}
            >
              <Input placeholder="e.g., Main Office Entrance" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="branch_id"
              label="Branch"
              rules={[{ required: true, message: 'Please select a branch' }]}
            >
              <Select placeholder="Select branch">
                {branches.map((branch) => (
                  <Option key={branch.id} value={branch.id}>
                    {branch.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="device_type"
              label="Device Type"
              rules={[{ required: true, message: 'Please select device type' }]}
            >
              <Select
                placeholder="Select device type"
                onChange={handleDeviceTypeChange}
              >
                <Option value="zkteco">ZKTeco</Option>
                <Option value="realand">Realand</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="location"
              label="Location"
            >
              <Input placeholder="e.g., 1st Floor, Main Entrance" />
            </Form.Item>
          </Col>

          <Col span={16}>
            <Form.Item
              name="ip_address"
              label="IP Address"
              rules={[
                { required: true, message: 'Please enter IP address' },
                {
                  pattern: /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/,
                  message: 'Please enter a valid IP address',
                },
              ]}
            >
              <Input placeholder="192.168.1.100" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="port"
              label="Port"
              rules={[
                { required: true, message: 'Please enter port' },
                {
                  type: 'number',
                  transform: (value) => Number(value),
                  min: 1,
                  max: 65535,
                  message: 'Port must be between 1 and 65535',
                },
              ]}
            >
              <Input type="number" placeholder="4370" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="serial_number"
              label="Serial Number"
            >
              <Input placeholder="Optional - Device serial number" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="is_active"
              label="Active Status"
              valuePropName="checked"
            >
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="push_users_to_device"
              label="Auto Push Users"
              valuePropName="checked"
              tooltip="Automatically push new employees to this device when they are created"
            >
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </Col>
        </Row>

        <div style={{ marginTop: 16, padding: 12, background: '#f0f2f5', borderRadius: 4 }}>
          <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
            <strong>Note:</strong> Make sure the device is accessible from this server's network.
            Default ports: ZKTeco (4370), Realand (80). When "Auto Push Users" is enabled,
            new employees in this branch will be automatically registered on this device.
          </p>
        </div>
      </Form>
    </Modal>
  );
};

export default BiometricDeviceModal;
